import { useState } from "react";
import { CheckIcon } from "@heroicons/react/20/solid";
import coachImage from "../../assets/images/image-LP_for_coaches.png";
import "./carousel.css";

const coachFeatures = [
  "Athlete Monitoring",
  "Training Load Tracking",
  "Wellness Insights",
  "Risk Alerts",
  "Team Dashboard",
  "Performance Analytics",
  "Custom Reports",
  "Data Export",
];

interface HeroCardProps {
  tag: string;
  headline: string;
  description: string;
  buttonVariant: "lime" | "dark";
  imageUrl?: string;
  imageBg: string;
}

const HeroCard = ({
  tag,
  headline,
  description,
  buttonVariant,
  imageUrl,
  imageBg,
}: HeroCardProps) => {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className="relative flex-1 rounded-md overflow-hidden cursor-pointer"
      style={{ minHeight: "560px" }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Background image / gradient */}
      <div
        className="absolute inset-0 bg-cover bg-center transition-transform duration-700 ease-out"
        style={{
          backgroundImage: imageUrl ? `url(${imageUrl})` : undefined,
          backgroundColor: imageUrl ? undefined : imageBg,
          transform: hovered ? "scale(1.04)" : "scale(1)",
        }}
      />

      {/* Gradient overlay - darkens bottom for text legibility */}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/10 to-transparent" />

      {/* Content */}
      <div className="absolute inset-0 flex flex-col justify-end p-8">
        {/* Bottom content */}
        <div className="flex flex-col">
          {/* Tag */}
          <span className="text-white/70 text-sm font-bold tracking-wide">
            {tag}
          </span>
          {/* Headline */}
          <h2
            className="text-white font-bold leading-tight"
            style={{ fontSize: "clamp(1.75rem, 3vw, 2.5rem)" }}
          >
            {headline}
          </h2>

          {/* Description */}
          <p className="text-white/70 text-sm leading-tight py-4 pt-2  max-w-2xl">
            {description}
          </p>
          {/* Feature Pills at the top */}
          <div className="overflow-hidden mb-4">
            <div className="flex animate-scroll">
              {/* First set of features */}
              <div className="flex gap-3 shrink-0">
                {coachFeatures.map((item, index) => (
                  <div
                    key={`first-${index}`}
                    className="flex items-center gap-2 bg-base-100/20 backdrop-blur-sm font-grotesk text-white px-4 py-2 rounded-3xl text-sm font-medium whitespace-nowrap border border-base-100/30"
                  >
                    <CheckIcon className="w-4 h-4 text-white" />
                    {item}
                  </div>
                ))}
              </div>
              {/* Duplicate set for infinite effect */}
              <div className="flex gap-3 shrink-0 ml-3">
                {coachFeatures.map((item, index) => (
                  <div
                    key={`second-${index}`}
                    className="flex items-center gap-2 bg-base-100/20 backdrop-blur-sm font-grotesk text-white px-4 py-2 rounded-3xl text-sm font-medium whitespace-nowrap border border-base-100/30"
                  >
                    <CheckIcon className="w-4 h-4 text-white" />
                    {item}
                  </div>
                ))}
              </div>
              {/* Third set to ensure seamless loop */}
              <div className="flex gap-3 shrink-0 ml-3">
                {coachFeatures.map((item, index) => (
                  <div
                    key={`third-${index}`}
                    className="flex items-center gap-2 bg-base-100/20 backdrop-blur-sm font-grotesk text-white px-4 py-2 rounded-3xl text-sm font-medium whitespace-nowrap border border-base-100/30"
                  >
                    <CheckIcon className="w-4 h-4 text-white" />
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* CTA Button */}
          {buttonVariant === "lime" ? (
            <button className="w-fit px-8 py-4 rounded-full font-semibold text-sm text-base-content transition-all duration-200 hover:scale-105 active:scale-95 bg-base-100">
              Register as an athlete
            </button>
          ) : (
            <a
              href="https://ams.peak.app"
              target="_blank"
              rel="noopener noreferrer"
              className="w-fit px-8 py-2 rounded-full font-semibold text-sm border border-base-100/30 text-base-content bg-base-100/95 backdrop-blur-sm transition-all duration-200 hover:text-white hover:bg-base-100/30 hover:scale-105 active:scale-95 bg-base-100"
            >
              Go to AMS
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default function HeroCards() {
  return (
    <div className="bg-base-100 flex items-center justify-center px-6 py-12">
      <div className="flex flex-col w-full max-w-5xl">
        <HeroCard
          tag="ams.peak.app"
          headline="For Coaches, Teams & Organisations."
          description="The Athlete Monitoring Portal (AMS) is an elite athlete monitoring platform, built to guide you from daily logs to clearer coaching decisions. Connect with your athlete's data to make better decision for peak performance."
          buttonVariant="dark"
          imageBg="#1a1f2e"
          imageUrl={coachImage}
        />
      </div>
    </div>
  );
}
